import React, { useState, useEffect } from 'react'
import { Row, Col, Container } from 'react-bootstrap'
import api from '../api/api'
import { formatNotificationDate } from '../utils/notificationDate'
import { useTranslation } from 'react-i18next'
import './AllNotifications.css'

const isCriticalNotification = (notification) =>
    String(notification?.field_status || '').toLowerCase() === 'critical' ||
    /critical|critique/i.test(`${notification?.object || ''} ${notification?.description || ''}`)

const AllNotifications = () => {
    const { t } = useTranslation()

    const [notifs, setNotifs] = useState([])
    const [filter, setFilter] = useState('all') // 'all' or 'unread'
    const [feedback, setFeedback] = useState(null)

   const getNotifications = async (mode = filter) => {
        try {
            if (mode === 'all') {
                const response = await api.get(`/notifications`);
                const notifications = response.data.data || [];
                setNotifs(notifications);
            } else {
                // unread: use UUID-based endpoint
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const userUuid = user.uid || user.id;
                const response = await api.get(`/notifications/unread/${userUuid}`);
                const notifications = response.data.notifications || [];
                setNotifs(notifications);
            }
        } catch (err) {
            console.error(err)
        }
    }


    useEffect(() => {
        getNotifications();
    }, [])


    const markAsRead = (itemUid) => {
        let notification_uid = itemUid

        api.post('/notification/viewed', { notification_uid })
            .then(response => {
                if (response.data.type === "success") {
                    setFeedback(t('notification_marked_read'));
                    setTimeout(() => setFeedback(null), 2500);
                    getNotifications(filter)

                }
            }).catch(err => {
                console.error(err)
            })
    }

    const markAsNotRead = (itemUid) => {
        let notification_uid = itemUid

        api.post('/notification/not-viewed', { notification_uid })
            .then(response => {
                if (response.data.type === "success") {
                    setFeedback(t('notification_marked_unread'));
                    setTimeout(() => setFeedback(null), 2500);
                    getNotifications(filter)

                }
            }).catch(err => {
                console.error(err)
            })
    }

    const softDelete = async (notif) => {
        // notif may have numeric id or uid; prefer numeric id for API delete
        const id = notif.id || notif.notification_id || null;
        if (!id) {
            console.warn("Impossible de supprimer : pas d'id numérique");
            return;
        }
        try {
            const resp = await api.delete(`/api/notifications/${id}`);
            if (resp.data && resp.data.success) {
                setFeedback(t('notification_deleted'));
                setTimeout(() => setFeedback(null), 2500);
                getNotifications(filter);
            } else {
                console.error('Erreur suppression:', resp.data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleFilterChange = (mode) => {
        setFilter(mode);
        getNotifications(mode);
    }

     return (
        <>
            <Container fluid className="main-content-container all-notifications-page p-4">
                <header className="notif-header">
                    <div className="notif-header-title">
                        <span><i className="fas fa-bell" aria-hidden="true" /></span>
                        <h1>{t('my_notifications')}</h1>
                    </div>
                    <div className="notif-filter-group" role="group" aria-label={t('filters', { defaultValue: 'Filtres' })}>
                        <button
                            type="button"
                            className={`notif-filter-btn ${filter === 'all' ? 'is-active' : ''}`}
                            aria-pressed={filter === 'all'}
                            onClick={() => handleFilterChange('all')}
                        >
                            {t('all')}
                        </button>
                        <button
                            type="button"
                            className={`notif-filter-btn ${filter === 'unread' ? 'is-active' : ''}`}
                            aria-pressed={filter === 'unread'}
                            onClick={() => handleFilterChange('unread')}
                        >
                            {t('unread')}
                        </button>
                    </div>
                </header>

                {feedback && (
                    <Row>
                        <Col>
                            <div className='alert alert-success notif-feedback' role='alert'>{feedback}</div>
                        </Col>
                    </Row>
                )}

                {notifs.length === 0 ? (
                    <div className="notif-empty-state">
                        <i className="fas fa-bell-slash" aria-hidden="true" />
                        <strong>{t('no_notifications_yet')}</strong>
                    </div>
                ) : (
                    <div className="notif-list">
                        {notifs.map(notif => {
                        const dateNotif = formatNotificationDate(notif.observed_at || notif.created_at);
                        const isUnread = notif.is_view === '0' || notif.is_view === 0;
                        const isCritical = isCriticalNotification(notif);

                        return (
                            <div
                                key={notif.uid}
                                className={`notif-card ${isUnread ? 'is-unread' : ''} ${isCritical ? 'notif--critical' : ''}`}
                            >
                                <div className="notif-card-top">
                                    <h6 className="notif-card-title">{t(notif.object, notif.object)}</h6>
                                    <p className="notif-card-date">{dateNotif}</p>
                                    <div className="notif-card-actions">
                                        {isUnread ? (
                                            <button
                                                type="button"
                                                title={t('mark_as_read')}
                                                aria-label={t('mark_as_read')}
                                                onClick={() => markAsRead(notif.uid)}
                                                className="notif-icon-btn"
                                            >
                                                <i className="fas fa-check" aria-hidden="true" />
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                title={t('mark_as_unread')}
                                                aria-label={t('mark_as_unread')}
                                                onClick={() => markAsNotRead(notif.uid)}
                                                className="notif-icon-btn"
                                            >
                                                <i className="fas fa-envelope" aria-hidden="true" />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            title={t('delete')}
                                            aria-label={t('delete')}
                                            onClick={() => softDelete(notif)}
                                            className="notif-icon-btn is-danger"
                                        >
                                            <i className="fas fa-trash" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>
                                <div className="notif-card-body">
                                    <span className="notif-card-icon">
                                        <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                                    </span>
                                    <p className="notif-card-text">{t(notif.description, notif.description)}</p>
                                </div>
                            </div>
                        );
                        })}
                    </div>
                )}
            </Container>
        </>
    );
};


export default AllNotifications
